import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import editorange from "../assets/images/icon/edit-orange.png";
import Input from "components/Input";
import Select from "components/Select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateWorkplace } from "services/workplaces";

const US_TIME_ZONES = [
    { label: "Eastern Time (ET) - New York, Miami", value: "America/New_York" },
    { label: "Central Time (CT) - Chicago, Dallas", value: "America/Chicago" },
    { label: "Mountain Time (MT) - Denver, Salt Lake City", value: "America/Denver" },
    { label: "Mountain Time - Arizona (no DST)", value: "America/Phoenix" },
    { label: "Pacific Time (PT) - Los Angeles, Seattle", value: "America/Los_Angeles" },
    { label: "Alaska Time (AKT)", value: "America/Anchorage" },
    { label: "Hawaii Time (HT) - Honolulu", value: "Pacific/Honolulu" },
];

const US_STATES = [
    { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
];

const buildForm = (wp) => ({
    name: wp?.name || '',
    addressLine1: wp?.addressLine1 || '',
    city: wp?.city || '',
    state: wp?.state || '',
    zip: wp?.zip || '',
    timeZone: wp?.timeZone || wp?.timezone || '',
    active: wp?.active ?? true,
});

function WorkPlaceDetailsModal({ workplace, open, onClose, defaultEditing = false }) {
    const [isEditing, setIsEditing] = useState(defaultEditing);
    const [form, setForm] = useState(buildForm(workplace));
    const [current, setCurrent] = useState(workplace);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (workplace) {
            setCurrent(workplace);
            setForm(buildForm(workplace));
            setIsEditing(defaultEditing);
        }
    }, [workplace, defaultEditing]);

    const mutation = useMutation({
        mutationFn: updateWorkplace,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workplaces'] });
            // Update local display immediately with saved values
            setCurrent(prev => ({
                ...prev,
                name: form.name,
                addressLine1: form.addressLine1,
                city: form.city,
                state: form.state,
                zip: form.zip,
                timeZone: form.timeZone,
                active: form.active,
            }));
            setIsEditing(false);
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        mutation.mutate({
            workplaceId: current.id,
            name: form.name,
            active: form.active,
            timeZone: form.timeZone || null,
            address: {
                street1: form.addressLine1,
                city: form.city,
                state: form.state,
                zipcode: form.zip,
            },
        });
    };

    const handleClose = () => {
        mutation.reset();
        onClose();
    };

    if (!current) return null;

    const address = [current.addressLine1, current.city, current.state, current.zip].filter(Boolean).join(', ');

    return (
        <Modal className='add-workplace-modal' show={open} onHide={handleClose} centered>
            <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
                <h4 className="sub-title mb-0">Workplace Detail</h4>
                <button type="button" className="close bg-transparent border-0" onClick={handleClose}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <Modal.Body>
                {!isEditing ? (
                    <>
                        <div className='d-flex justify-content-between align-items-center'>
                            <div>
                                <p className='gray-title my-2'>Workplace Id</p>
                                <h6 className='id-title fw-medium text-black m-0'>{current.id}</h6>
                            </div>
                            <div>
                                <img className='cursor-pointer' src={editorange} alt="edit" onClick={() => setIsEditing(true)} />
                            </div>
                        </div>
                        <div className='border-bottom my-3 pb-4'>
                            <h2 className='sub-title'>General Information</h2>
                            <div className='d-flex workplace-detail-section mt-4'>
                                <div>
                                    <p className='gray-title my-2'>Name</p>
                                    <h6 className='id-title fw-medium text-black m-0'>{current.name || '—'}</h6>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>Active Status</p>
                                    <button className='active-button py-1 px-4'>{current.active ? 'Active' : 'Inactive'}</button>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>Address</p>
                                    <h6 className='id-title fw-medium text-black m-0'>{address || '—'}</h6>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>Time Zone</p>
                                    <h6 className='id-title fw-medium text-black m-0'>{current.timeZone || current.timezone || '—'}</h6>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleSave}>
                        <div className='row mt-3'>
                            <div className='col-12'>
                                <Input label="Workplace Name" fullWidth variant='flat' name='name' value={form.name} onChange={handleChange} />
                            </div>
                            <div className='col-12 mt-3'>
                                <Input label="Street 1" fullWidth variant='flat' name='addressLine1' value={form.addressLine1} onChange={handleChange} />
                            </div>
                            <div className='col-lg-4 mt-3'>
                                <Input label="City" fullWidth variant='flat' name='city' value={form.city} onChange={handleChange} />
                            </div>
                            <div className='col-lg-4 mt-3'>
                                <Select label="State" fullWidth placeholder='Select State' options={US_STATES} name='state' value={form.state} onChange={handleChange} />
                            </div>
                            <div className='col-lg-4 mt-3'>
                                <Input label="Zip Code" fullWidth variant='flat' name='zip' value={form.zip} onChange={handleChange} />
                            </div>
                            <div className='col-lg-6 mt-3'>
                                <Select label="Time Zone" fullWidth placeholder='Select time zone' options={US_TIME_ZONES} name='timeZone' value={form.timeZone} onChange={handleChange} />
                            </div>
                        </div>
                        {mutation.isError && (
                            <p className="text-danger mt-3">Failed to update workplace. Please try again.</p>
                        )}
                        <div className='d-flex gap-2 mt-4'>
                            <button type='submit' className='button-orange py-2 px-4 rounded-pill' disabled={mutation.isPending}>
                                {mutation.isPending ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button type='button' className='btn btn-outline-secondary rounded-pill py-2 px-4' onClick={() => setIsEditing(false)} disabled={mutation.isPending}>
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default WorkPlaceDetailsModal;
